import classNames from "classnames"
import React, { Children } from "react"
import type { Swiper as SwiperType } from "swiper"
import { Autoplay, FreeMode } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import "./styles.css"

type Props = {
  children: React.ReactNode
  className?: string
  slideClassName?: string
  autoplay?: boolean
  useLoopBuffer?: boolean
}

/**
 * @todo We're using this for TestimonialSwiper and SocialProofing.
 * We should refactor this to be more generic.
 */
const SocialProofingSlider = ({
  children,
  className,
  slideClassName,
  autoplay,
  useLoopBuffer,
}: Props) => {
  const baseSlides = Children.toArray(children)
  const shouldUseLoopBuffer = Boolean(useLoopBuffer && baseSlides.length > 1)
  const useSeamlessWrap = shouldUseLoopBuffer && !autoplay
  const slideSets = useSeamlessWrap ? 5 : shouldUseLoopBuffer ? 3 : 1
  const slides = Array.from({ length: slideSets }, () => baseSlides).flat()
  const middleSetIndex = Math.floor(slideSets / 2)
  const resetIndex = shouldUseLoopBuffer
    ? middleSetIndex * baseSlides.length
    : 0

  const resetToMiddleSlide = (swiper: SwiperType) => {
    if (!shouldUseLoopBuffer || useSeamlessWrap) {
      return
    }

    if (!swiper.isBeginning && !swiper.isEnd) {
      return
    }

    const activeSlideIndex = swiper.activeIndex % baseSlides.length
    swiper.slideTo(resetIndex + activeSlideIndex, 0, false)
  }

  const wrapToMiddleSet = (swiper: SwiperType) => {
    if (!useSeamlessWrap) {
      return
    }

    const setWidth = swiper.slidesGrid[baseSlides.length] - swiper.slidesGrid[0]

    if (!setWidth) {
      return
    }

    const center = -setWidth * middleSetIndex
    let translate = swiper.translate

    while (translate > center + setWidth / 2) {
      translate -= setWidth
    }
    while (translate < center - setWidth / 2) {
      translate += setWidth
    }

    if (translate === swiper.translate) {
      return
    }

    swiper.translateTo(translate, 0, false, false)
    swiper.updateProgress()
    swiper.updateSlidesClasses()
  }

  return (
    <Swiper
      modules={[Autoplay, FreeMode]}
      id="social-proofing"
      spaceBetween={20}
      slidesOffsetBefore={20}
      slidesOffsetAfter={20}
      slidesPerView="auto"
      initialSlide={useSeamlessWrap ? resetIndex : 0}
      loop={!useSeamlessWrap}
      loopAdditionalSlides={shouldUseLoopBuffer ? baseSlides.length * 2 : 0}
      autoplay={
        autoplay
          ? {
              delay: 0,
              disableOnInteraction: false,
            }
          : false
      }
      freeMode
      grabCursor
      watchOverflow
      breakpoints={{
        900: {
          spaceBetween: 24,
        },
        1200: {
          spaceBetween: 32,
        },
      }}
      speed={useSeamlessWrap ? 300 : 25000}
      onTouchEnd={(swiper) => {
        window.setTimeout(() => {
          resetToMiddleSlide(swiper)
          if (!swiper.animating) {
            wrapToMiddleSet(swiper)
          }
        }, 0)
      }}
      onTransitionEnd={(swiper) => {
        resetToMiddleSlide(swiper)
        wrapToMiddleSet(swiper)
      }}
      onReachEnd={wrapToMiddleSet}
      onReachBeginning={wrapToMiddleSet}
      className={classNames("overflow-visible", className)}
    >
      {slides.map((child, index) => (
        <SwiperSlide
          key={`swiper-slide-${index}`}
          className={classNames(
            "grid h-auto w-[375px] last:mr-[calc(100vw-5.33vw-375px)]",
            useLoopBuffer && "sm:!w-[375px]",
            slideClassName
          )}
        >
          {child}
        </SwiperSlide>
      ))}
    </Swiper>
  )
}

export default SocialProofingSlider
